import type { ScenarioOrder } from '../../../lib/planningData';
import type { LoadEvent, TimeData } from '../../core/TimeData';
import { isLoadEventTimeData } from '../../core/TimeData';
import { EventCategory } from '../../core/Core';
import { Currency, FreightType } from '../../../graphql';
import { getOrderCoaDetails } from '../../shipment/getOderCoaDetails';
import type { PricedCoa } from '../../shipment/costs/maxCoaFreightRate';
import { prepareCoaInput } from '../../shipment/costs/maxCoaFreightRate';
import { getFreightCost } from '../../shipment/costs/freightCost';
import type { WorldscaleFreightRateMap } from '../../../lib/worldscaleFreight';
import { convertToDefaultCurrency } from '../../../state/zustand';
import { compact } from 'lodash';
import type { ClientId } from '../../core/ClientId';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../../Scenario';

type VoyageCharterOfOrder = NonNullable<
  NonNullable<
    ScenarioOrder['outgoingVoyageCharterRelation']
  >['voyageCharterByVoyageCharterId']
>;

export type CoasById = Map<number, PricedCoa>;

const coaOfLoadEvent = (
  td: TimeData<LoadEvent>,
  coasById: CoasById,
): PricedCoa | null =>
  td.event.category === EventCategory.COA
    ? (coasById.get(td.event.entry.coaId) ?? null)
    : null;

const outgoingVcFreightOf = (order: ScenarioOrder) =>
  order.outgoingVoyageCharterRelation?.voyageCharterOrderFreightsByOrderVoyageCharterRelationId?.nodes.at(
    0,
  ) ?? null;

/** Resolve the COA rate for one load event, matching assortment and route the
 * same way the Voyage P&L panel does. */
const coaDetailsOf = (td: TimeData<LoadEvent>, coa: PricedCoa) =>
  getOrderCoaDetails(
    {
      assortmentId: td.event.order.assortmentId ?? null,
      assortmentGroupId:
        td.event.order.assortmentByAssortmentId?.assortmentGroup ?? null,
      loadPortId: td.event.order.loadPortId ?? null,
      dischargePortId: td.event.order.dischargePortId,
    },
    { mass: td.mass, volume: td.volume },
    prepareCoaInput(coa),
    td.event.order.outgoingVoyageCharterRelation?.coaRouteId,
  );

/**
 * Cargo carried on our own vessel owes no freight. Cargo carried under a
 * contract does, whether that contract is a COA, a spot fixture or a relet —
 * a relet being a spot fixture seen from the shipowner's side.
 */
const owesFreight = (td: TimeData<LoadEvent>): boolean =>
  td.event.category !== EventCategory.TC ||
  !!td.event.order.outgoingVoyageCharterRelation;

/**
 * Highest COA rate across the voyage's cargo, for COAs priced that way. Mirrors
 * getMaxCoaFreightRateByCoaId, which the Voyage P&L panel applies.
 */
function maxCoaRateByCoaId(
  loadEvents: TimeData<LoadEvent>[],
  coasById: CoasById,
): Map<number, number> {
  const result = new Map<number, number>();

  for (const td of loadEvents) {
    const coa = coaOfLoadEvent(td, coasById);
    if (!coa?.useHighestFreightRate) continue;

    const price = coaDetailsOf(td, coa).coaFreight?.price;
    if (price == null) continue;

    const rate = Number(price);
    const prev = result.get(coa.id);
    if (prev === undefined || rate > prev) result.set(coa.id, rate);
  }

  return result;
}

/**
 * Freight the voyage owes, following calculateFreightCosts in the Voyage P&L
 * panel: a voyage charter lumpsum is charged on top of that fixture's cargo
 * rates, and lumpsums and extra freights carry their own currency while cargo
 * rates are converted.
 *
 * Measured freight, which the panel prefers over planned, needs cargo
 * measurements; those are not fetched for the timeline.
 *
 * Freight set at the shipment level replaces the per-cargo rates of that
 * shipment's cargo and is charged at face amount, as
 * calculateShipmentFreightCosts does for the panel's cost tables.
 */
export function calculateTimelineFreightCosts(
  events: TimeData[],
  coasById: CoasById = new Map(),
  worldscaleFreightRates?: WorldscaleFreightRateMap,
  shipmentFreightByShipmentClientId?: Map<ClientId, ScenarioShipmentFreight>,
  shipmentExtraFreightsByShipmentClientId?: Map<
    ClientId,
    ScenarioShipmentExtraFreight[]
  >,
): number {
  const loadEvents = events.filter(isLoadEventTimeData);
  const maxRateByCoaId = maxCoaRateByCoaId(loadEvents, coasById);

  /** Charters whose lumpsum a COA-priced cargo has taken out of play. */
  const ignoredVcIds = new Set<number>();
  const lumpsumVcs = new Map<number, VoyageCharterOfOrder>();

  const loadedByShipment = new Map<
    ClientId,
    { mass: number; volume: number; pcs: number }
  >();

  let total = 0;

  for (const td of loadEvents) {
    if (!owesFreight(td)) continue;

    const { order } = td.event;

    const shipmentFreight = order.shipmentId
      ? shipmentFreightByShipmentClientId?.get(order.shipmentId)
      : undefined;
    if (
      order.shipmentId &&
      (shipmentFreight ||
        shipmentExtraFreightsByShipmentClientId?.get(order.shipmentId)?.length)
    ) {
      const loaded = loadedByShipment.get(order.shipmentId) ?? {
        mass: 0,
        volume: 0,
        pcs: 0,
      };
      loaded.mass += td.mass ?? 0;
      loaded.volume += td.volume ?? 0;
      loaded.pcs += td.pcs ?? 0;
      loadedByShipment.set(order.shipmentId, loaded);
    }
    const vcRelation = order.outgoingVoyageCharterRelation;
    const voyageCharter = vcRelation?.voyageCharterByVoyageCharterId ?? null;
    const vcFreight = outgoingVcFreightOf(order);
    const vcRate = vcFreight?.freightAmount ?? null;

    const assignedCoa = coaOfLoadEvent(td, coasById);

    // A voyage charter only displaces the COA rate when it is for that same COA
    // and vessel and actually carries a rate.
    const isCoaOverriddenByVoyageCharter =
      td.event.category === EventCategory.COA &&
      td.event.entry.vesselId === voyageCharter?.vesselId &&
      vcRate !== null &&
      +vcRate > 0 &&
      assignedCoa?.id ===
        voyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes.at(0)
          ?.coaId;

    const { coaAssortment, coaFreight } = assignedCoa
      ? coaDetailsOf(td, assignedCoa)
      : { coaAssortment: null, coaFreight: null };

    // Cargo the contract does not price gets no COA freight — not even the
    // voyage max; it falls back to charter freight or none.
    const useCoaRate =
      coaFreight &&
      (!isCoaOverriddenByVoyageCharter || assignedCoa?.useHighestFreightRate);

    const freight = useCoaRate
      ? {
          freightAmount:
            (assignedCoa && maxRateByCoaId.get(assignedCoa.id)) ??
            coaFreight.price,
          freightUnit: coaAssortment?.paymentUnit,
        }
      : vcFreight;

    const { freightAmount, freightUnit } = freight ?? {};

    // Shipment-level freight replaces this cargo's rate and per-cargo extra
    // freights; the charter lumpsum below still applies, as in the panel.
    if (!shipmentFreight) {
      if (freightAmount && freightUnit) {
        const value = getFreightCost(
          td.mass,
          td.volume,
          td.pcs,
          +freightAmount,
          freightUnit,
          worldscaleFreightRates,
          order,
        );
        const currency =
          (isCoaOverriddenByVoyageCharter || !assignedCoa
            ? voyageCharter?.currency
            : undefined) ??
          (!isCoaOverriddenByVoyageCharter && assignedCoa
            ? assignedCoa.currency
            : undefined) ??
          Currency.EUR;

        if (value) total += convertToDefaultCurrency(value, currency) ?? 0;
      }

      if (!assignedCoa || isCoaOverriddenByVoyageCharter) {
        for (const extra of compact(
          voyageCharter?.voyageCharterExtraFreightsByVoyageCharterId?.nodes ??
            [],
        )) {
          if (extra.type !== FreightType.RATE || !freightUnit) continue;
          const amount = getFreightCost(
            td.mass,
            td.volume,
            null,
            +extra.amount,
            freightUnit,
            worldscaleFreightRates,
            order,
          );
          if (amount) total += amount;
        }
      }
    }

    if (assignedCoa && !isCoaOverriddenByVoyageCharter && voyageCharter) {
      ignoredVcIds.add(voyageCharter.id);
    }

    if (
      voyageCharter &&
      !lumpsumVcs.has(voyageCharter.id) &&
      !ignoredVcIds.has(voyageCharter.id)
    ) {
      lumpsumVcs.set(voyageCharter.id, voyageCharter);
    }
  }

  for (const voyageCharter of lumpsumVcs.values()) {
    for (const extra of compact(
      voyageCharter.voyageCharterExtraFreightsByVoyageCharterId?.nodes ?? [],
    )) {
      if (extra.type !== FreightType.LUMPSUM || !extra.amount) continue;
      total += +extra.amount;
    }

    // An unparseable lumpsum would spread NaN through cost and profit.
    const lumpsum = Number(voyageCharter.lumpsum ?? NaN);
    if (!isNaN(lumpsum)) total += lumpsum;
  }

  // Face amounts with no currency conversion, as the panel charges them.
  loadedByShipment.forEach((loaded, shipmentClientId) => {
    const shipmentFreight =
      shipmentFreightByShipmentClientId?.get(shipmentClientId);
    const extras =
      shipmentExtraFreightsByShipmentClientId?.get(shipmentClientId) ?? [];
    for (const entry of shipmentFreight
      ? [shipmentFreight, ...extras]
      : extras) {
      const value = getFreightCost(
        loaded.mass,
        loaded.volume,
        loaded.pcs,
        Number(entry.freightAmount),
        entry.freightUnit,
      );
      if (value) total += value;
    }
  });

  return total;
}
